import React from "react";
import { TabPanel } from "../TabPanel";
import ReactApexChart from "react-apexcharts";
import { Box, Card, Typography, Grid } from "@mui/material";
import { styled } from "@mui/material/styles";

// Styled Components
const DashboardCard = styled(Card)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.95)',
  borderRadius: 20,
  padding: theme.spacing(3),
  boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.1)',
  border: '1px solid rgba(255, 255, 255, 0.18)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 12px 40px 0 rgba(31, 38, 135, 0.15)',
  },
}));

const ChartTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.25rem',
  fontWeight: 600,
  marginBottom: theme.spacing(3),
  color: theme.palette.text.primary,
}));

export const PassengerDriverTab = ({
  currentTab,
  tripsByMonthData,
  revenuesByMonthData,
  passengersByMonthData,
  driversByMonthData,
  driversByRegistrationData,
}) => {
  // Chart configurations
  const lineChartOptions = {
    chart: {
      type: 'area',
      toolbar: {
        show: false,
      },
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800,
      },
    },
    stroke: {
      curve: 'smooth',
      width: 3,
    },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.7,
        opacityTo: 0.2,
        stops: [0, 90, 100],
      },
    },
    dataLabels: {
      enabled: false,
    },
    grid: {
      borderColor: '#f1f1f1',
      xaxis: {
        lines: {
          show: false,
        },
      },
    },
    colors: ['#4361ee', '#f72585'],
    xaxis: {
      categories: tripsByMonthData.labels,
      labels: {
        style: {
          colors: '#555',
          fontSize: '12px',
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: '#555',
          fontSize: '12px',
        },
      },
    },
    tooltip: {
      theme: 'dark',
      x: {
        show: false,
      },
    },
  };

  const barChartOptions = {
    chart: {
      type: 'bar',
      toolbar: {
        show: false,
      },
      animations: {
        enabled: true,
        dynamicAnimation: {
          speed: 800,
        },
      },
    },
    plotOptions: {
      bar: {
        borderRadius: 8,
        columnWidth: '60%',
        distributed: true,
      },
    },
    dataLabels: {
      enabled: false,
    },
    colors: ['#4361ee', '#f72585', '#7209b7', '#3a0ca3', '#4cc9f0'],
    xaxis: {
      categories: passengersByMonthData.labels,
      labels: {
        style: {
          colors: '#555',
          fontSize: '12px',
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: '#555',
          fontSize: '12px',
        },
      },
    },
    grid: {
      borderColor: '#f1f1f1',
    },
    tooltip: {
      theme: 'dark',
    },
  };

  return (
    <TabPanel value={currentTab} index={1}>
      <Box sx={{ p: 3, background: '#f8f9fa' }}>
        <Grid container spacing={3}>
          {/* Trips By Date */}
          <Grid item xs={12} md={6}>
            <DashboardCard>
              <ChartTitle>Trips By Date</ChartTitle>
              <ReactApexChart
                options={lineChartOptions}
                series={[
                  {
                    name: 'Trips',
                    data: tripsByMonthData.datasets[0].data,
                  },
                ]}
                type="area"
                height={350}
              />
            </DashboardCard>
          </Grid>

          {/* Revenues By Date */}
          <Grid item xs={12} md={6}>
            <DashboardCard>
              <ChartTitle>Revenues By Date</ChartTitle>
              <ReactApexChart
                options={{
                  ...lineChartOptions,
                  colors: ['#f72585'],
                }}
                series={[
                  {
                    name: 'Revenue',
                    data: revenuesByMonthData.datasets[0].data,
                  },
                ]}
                type="area"
                height={350}
              />
            </DashboardCard>
          </Grid>

          {/* Passengers By Month */}
          <Grid item xs={12} md={6}>
            <DashboardCard>
              <ChartTitle>Passengers By Month</ChartTitle>
              <ReactApexChart
                options={barChartOptions}
                series={[
                  {
                    name: 'Passengers',
                    data: passengersByMonthData.datasets[0].data,
                  },
                ]}
                type="bar"
                height={350}
              />
            </DashboardCard>
          </Grid>

          {/* Drivers By Month */}
          <Grid item xs={12} md={6}>
            <DashboardCard>
              <ChartTitle>Drivers By Month</ChartTitle>
              <ReactApexChart
                options={{
                  ...barChartOptions,
                  colors: ['#7209b7'],
                }}
                series={[
                  {
                    name: 'Drivers',
                    data: driversByMonthData.datasets[0].data,
                  },
                ]}
                type="bar"
                height={350}
              />
            </DashboardCard>
          </Grid>

          {/* Drivers Registration Statistics */}
          <Grid item xs={12} md={6}>
            <DashboardCard>
              <ChartTitle>Drivers Registration Statistics</ChartTitle>
              <ReactApexChart
                options={{
                  ...barChartOptions,
                  chart: {
                    ...barChartOptions.chart,
                    type: 'bar',
                  },
                  plotOptions: {
                    ...barChartOptions.plotOptions,
                    bar: {
                      ...barChartOptions.plotOptions.bar,
                      horizontal: true,
                      borderRadius: 6,
                    },
                  },
                }}
                series={[
                  {
                    name: 'Registrations',
                    data: driversByRegistrationData.datasets[0].data,
                  },
                ]}
                type="bar"
                height={350}
              />
            </DashboardCard>
          </Grid>
        </Grid>
      </Box>
    </TabPanel>
  );
};

export default PassengerDriverTab;